import BigNumber from 'bignumber.js';
import {
  NumberInput as MantineNumberInput,
  NumberInputProps as MantineNumberInputProps,
  Button,
  Text,
} from '@mantine/core';
import cn from 'clsx';
import { useRef, useEffect } from 'react';
import { Token } from 'src/components/Token/Token';
import { fixLeadingZero } from 'src/utils';
import { MAX_DECIMALS_DISPLAY } from 'src/constants';

export type NumberInputProps = {
  value?: string | number | BigNumber;
  label?: React.ReactNode;
  tokenSymbol?: string;
  tokenAddress?: string;
  leftSection?: React.ReactNode;
  leftSectionWidth?: MantineNumberInputProps['leftSectionWidth'];
  leftSectionProps?: MantineNumberInputProps['leftSectionProps'];
  rightSection?: React.ReactNode;
  rightSectionWidth?: MantineNumberInputProps['rightSectionWidth'];
  rightSectionProps?: MantineNumberInputProps['rightSectionProps'];
  inputProps?: MantineNumberInputProps;
  isReversedWrapperOrder?: boolean;
  allowDecimals?: boolean;
  isLoading?: boolean;
  isInvalid?: boolean;
  autoFocus?: boolean;
  readOnly?: boolean;
  onChange: (value?: string) => void;
  onMaxClick?: () => void;
  onClick?: () => void;
};

export function NumberInput({
  value,
  label,
  tokenSymbol,
  tokenAddress,
  leftSection: leftSectionFromProps,
  leftSectionWidth: leftSectionWidthFromProps,
  leftSectionProps,
  rightSection,
  rightSectionWidth,
  rightSectionProps,
  inputProps,
  isReversedWrapperOrder,
  isLoading,
  isInvalid,
  autoFocus,
  readOnly,
  onChange,
  onMaxClick,
  onClick,
}: NumberInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const tokenIcon = tokenSymbol ? (
    <Token.Icon symbol={tokenSymbol} size={32} />
  ) : tokenAddress ? (
    <Token.Icon address={tokenAddress} size={32} />
  ) : undefined;

  const leftSection = leftSectionFromProps || tokenIcon;
  const leftSectionWidth = leftSectionWidthFromProps || tokenIcon ? 64 : 0;

  const _leftSectionProps = isReversedWrapperOrder
    ? rightSectionProps
    : leftSectionProps;
  const _rightSectionProps = isReversedWrapperOrder
    ? leftSectionProps
    : rightSectionProps;

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, []);

  return (
    <MantineNumberInput
      label={label}
      hideControls
      leftSection={isReversedWrapperOrder ? rightSection : leftSection}
      leftSectionWidth={
        isReversedWrapperOrder ? rightSectionWidth : leftSectionWidth
      }
      leftSectionProps={{
        ..._leftSectionProps,
        className: cn('px-4', _leftSectionProps?.className),
      }}
      rightSection={isReversedWrapperOrder ? leftSection : rightSection}
      rightSectionWidth={
        isReversedWrapperOrder ? leftSectionWidth : rightSectionWidth
      }
      rightSectionProps={{
        ..._rightSectionProps,
        className: cn('px-4', _rightSectionProps?.className),
      }}
      {...(onMaxClick && {
        rightSection: (
          <Button
            h={26}
            px={12}
            className="rounded-[10px]"
            style={{
              background:
                'linear-gradient(225deg,rgba(58,236,167,0.12) 6.91%,rgba(154,238,230,0.12) 67.21%)',
            }}
            onClick={() => {
              onMaxClick();
            }}
          >
            <Text variant="gradient" fz={14} fw={700}>
              Max
            </Text>
          </Button>
        ),
        rightSectionWidth: 54,
        rightSectionProps: {
          ...rightSectionProps,
          className: cn('px-0', rightSectionProps?.className),
        },
      })}
      min={0}
      max={999_999_999_999}
      clampBehavior="strict"
      defaultValue={0}
      thousandSeparator=","
      decimalScale={MAX_DECIMALS_DISPLAY}
      allowNegative={false}
      ref={inputRef}
      size="md"
      placeholder="0.0"
      trimLeadingZeroesOnBlur={false}
      readOnly={readOnly}
      {...inputProps}
      className={cn(
        inputProps?.className,
        isLoading && 'pointer-events-none animate-pulse'
      )}
      classNames={{
        ...inputProps?.classNames,
        input: cn(
          // @ts-ignore
          inputProps?.classNames?.input,
          tokenIcon && (isReversedWrapperOrder ? 'pr-14' : 'pl-14'),
          isReversedWrapperOrder && 'text-right',
          isInvalid && '!text-[#FF3E55]'
        ),
      }}
      value={String(value) || '0'}
      onChange={(v) => {
        if (v === '.') {
          return onChange('0.');
        }

        if (!v || v === 'undefined') {
          onChange(undefined);
        } else {
          onChange(fixLeadingZero(String(v)));
        }

        if (v === '' && inputRef.current) {
          inputRef.current.blur();

          setTimeout(() => {
            inputRef.current?.focus();
          }, 0);
        }
      }}
      onClick={onClick}
    />
  );
}
